import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";

import AdminLayout from "./components/AdminLayout";
import DriverLayout from "./components/DriverLayout";

import AdminLogin from "./pages/admin/AdminLogin";
import Dashboard from "./pages/admin/Dashboard";
import Drivers from "./pages/admin/Drivers";
import DriverDetail from "./pages/admin/DriverDetail";
import Vehicles from "./pages/admin/Vehicles";
import VehicleDetail from "./pages/admin/VehicleDetail";
import DriverVehicleReport from "./pages/admin/DriverVehicleReport";
import Compliance from "./pages/admin/Compliance";
import Messages from "./pages/admin/Messages";
import Settings from "./pages/admin/Settings";

import DriverLogin from "./pages/driver/DriverLogin";
import DriverDashboard from "./pages/driver/DriverDashboard";
import DriverProfile from "./pages/driver/DriverProfile";
import SignDocuments from "./pages/driver/SignDocuments";
import DriverApplicationForm from "./pages/driver/DriverApplicationForm";
import IcaForm from "./pages/driver/IcaForm";
import Wc43Form from "./pages/driver/Wc43Form";
import UploadDocuments from "./pages/driver/UploadDocuments";
import DriverMessages from "./pages/driver/DriverMessages";
import DriverEarnings from "./pages/driver/DriverEarnings";

import Landing from "./pages/Landing";
import { adminApi } from "./services/adminApi";
import { driverApi } from "./services/driverApi";

// ─── Admin section ────────────────────────────────────────────────────────────

function AdminSection() {
  const [admin, setAdmin] = useState(undefined); // undefined = loading
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    adminApi.me()
      .then(({ admin: a }) => setAdmin(a))
      .catch(() => setAdmin(null));
  }, []);

  if (admin === undefined) return <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">Loading…</div>;

  if (admin === null) {
    if (location.pathname !== "/enrollme/admin/login") {
      return <Navigate to="/enrollme/admin/login" replace />;
    }
    return <AdminLogin onLogin={(a) => { setAdmin(a); navigate("/enrollme/admin/dashboard"); }} />;
  }

  if (location.pathname === "/enrollme/admin/login") {
    return <Navigate to="/enrollme/admin/dashboard" replace />;
  }

  return (
    <AdminLayout admin={admin} onLogout={() => setAdmin(null)}>
      <Routes>
        <Route path="dashboard" element={<Dashboard admin={admin} />} />
        <Route path="drivers" element={<Drivers />} />
        <Route path="drivers/:id" element={<DriverDetail />} />
        <Route path="vehicles" element={<Vehicles />} />
        <Route path="vehicles/:id" element={<VehicleDetail />} />
        <Route path="report" element={<DriverVehicleReport />} />
        <Route path="compliance" element={<Compliance />} />
        <Route path="messages" element={<Messages admin={admin} />} />
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </AdminLayout>
  );
}

// ─── Driver section ───────────────────────────────────────────────────────────

function DriverSection() {
  const [driver, setDriver] = useState(undefined);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    driverApi.me()
      .then(({ driver: d }) => setDriver(d))
      .catch(() => setDriver(null));
  }, []);

  if (driver === undefined) return <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">Loading…</div>;

  if (driver === null) {
    if (location.pathname !== "/enrollme/driver/login") {
      return <Navigate to="/enrollme/driver/login" replace />;
    }
    return <DriverLogin onLogin={(d) => { setDriver(d); navigate("/enrollme/driver/dashboard"); }} />;
  }

  if (location.pathname === "/enrollme/driver/login") {
    return <Navigate to="/enrollme/driver/dashboard" replace />;
  }

  return (
    <DriverLayout driver={driver} onLogout={() => setDriver(null)}>
      <Routes>
        <Route path="dashboard" element={<DriverDashboard driver={driver} />} />
        <Route path="profile" element={<DriverProfile driver={driver} />} />
        <Route path="sign" element={<SignDocuments driver={driver} />} />
        <Route path="application" element={<DriverApplicationForm driver={driver} />} />
        <Route path="ica" element={<IcaForm driver={driver} />} />
        <Route path="wc43" element={<Wc43Form />} />
        <Route path="uploads" element={<UploadDocuments />} />
        <Route path="earnings" element={<DriverEarnings />} />
        <Route path="messages" element={<DriverMessages driver={driver} />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </DriverLayout>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/enrollme/admin/login" element={<AdminSection />} />
        <Route path="/enrollme/admin/*" element={<AdminSection />} />
        <Route path="/enrollme/driver/login" element={<DriverSection />} />
        <Route path="/enrollme/driver/*" element={<DriverSection />} />
        <Route path="/enrollme" element={<Landing />} />
        <Route path="/" element={<Navigate to="/enrollme" replace />} />
        <Route path="*" element={<Navigate to="/enrollme" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
